import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, catchError, throwError } from 'rxjs';
import { AiOrderParserConfig } from '../../models/ai-order-parser-config';
import { AiOrderParserSettings } from '../../models/ai-order-parser-settings';
import { AiOrderParserWarning } from '../../models/ai-order-parser-warning';

export interface ParsedOrderRow {
  productId: string;
  quantity: number;
  extra?: Record<string, unknown>;
}

export interface ParsedOrder {
  id: string;
  deliveryCustomerId?: string;
  billingCustomerId?: string;
  extra?: { [key: string]: any };
  rows: ParsedOrderRow[];
}

export interface PdfAiParserResponse {
  order: ParsedOrder;
  success: boolean;
  message: string;
  warnings: AiOrderParserWarning[];
}

export interface PdfAiParserRequest {
  file: File;
  config?: AiOrderParserConfig;
}

@Injectable({
  providedIn: 'root'
})
export class PdfAiParserService {

  private readonly configsUrl = 'api/ai-order-parser/configs';
  private readonly settingsUrl = 'api/ai-order-parser/settings';

  constructor(private http: HttpClient) { }

  /**
   * Parse an order from a PDF document using AI
   */
  parseOrder(request: PdfAiParserRequest): Observable<PdfAiParserResponse> {
    const formData = new FormData();
    formData.append('file', request.file, request.file.name);

    if (request.config) {
      const configBlob = new Blob([JSON.stringify(request.config)], { type: 'application/json' });
      formData.append('config', configBlob);
    }

    return this.http.post<PdfAiParserResponse>('api/ai-order-parser/parse', formData).pipe(
      catchError(error => {
        console.error('Order parser error:', error);
        return throwError(() => error);
      })
    );
  }

  /**
   * Ask the AI to draft a suggested `aiInstructions` prompt from a sample document and a
   * plain-language description of it. The sample is transient — it is only sent for this call
   * and never stored server-side.
   */
  generateInstructions(file: File, description: string): Observable<{ aiInstructions: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const trimmed = (description || '').trim();
    if (trimmed) {
      formData.append('description', trimmed);
    }
    return this.http.post<{ aiInstructions: string }>(`${this.configsUrl}/generate-instructions`, formData);
  }

  listConfigs(): Observable<AiOrderParserConfig[]> {
    // maxAge:0 makes the platform CacheInterceptor treat any cached copy as expired,
    // so the list reflects configs saved/deleted this session without a full reload.
    return this.http.get<AiOrderParserConfig[]>(this.configsUrl, { headers: { maxAge: '0' } });
  }

  getConfig(id: string): Observable<AiOrderParserConfig> {
    return this.http.get<AiOrderParserConfig>(`${this.configsUrl}/${encodeURIComponent(id)}`, { headers: { maxAge: '0' } });
  }

  saveConfig(config: AiOrderParserConfig): Observable<AiOrderParserConfig> {
    return this.http.post<AiOrderParserConfig>(this.configsUrl, config);
  }

  deleteConfig(id: string): Observable<void> {
    return this.http.delete<void>(`${this.configsUrl}/${encodeURIComponent(id)}`);
  }

  /** Current parser settings. Readable by any parser user; edited only by admins. */
  getSettings(): Observable<AiOrderParserSettings> {
    // maxAge:0 so a fresh save is reflected without a full cache flush (matches config reads).
    return this.http.get<AiOrderParserSettings>(this.settingsUrl, { headers: { maxAge: '0' } });
  }

  /** Persist parser settings. Sends the whole object incl. `@class` (admin-only on the server). */
  updateSettings(settings: AiOrderParserSettings): Observable<AiOrderParserSettings> {
    return this.http.put<AiOrderParserSettings>(this.settingsUrl, settings);
  }
}
